import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';

export default function ManageBookingsInline({ onClose }){
  const [filter, setFilter] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();

  useEffect(()=>{
    let mounted = true;
    async function fetchAll(){
      setLoading(true); setError(null);
      try{
        const res = await api.get('/clerk/bookings', { params: { limit: 1000 } });
        const data = Array.isArray(res.data) ? res.data : (res.data && res.data.data ? res.data.data : []);
        if (!mounted) return;
        setResults(data);
      }catch(err){
        if (!mounted) return;
        setError(err?.response?.data?.message || err.message || 'Failed to load bookings');
        setResults([]);
      }finally{ if (mounted) setLoading(false); }
    }
    fetchAll();
    return ()=>{ mounted = false; };
  },[]);

  function applyFilter(){
    if (!filter) return; // no-op
    const q = filter.toLowerCase();
    setResults(prev => prev.filter(r => String(r.id).includes(q) || String(r.user_id).includes(q) || String(r.event_id).includes(q) || (r.user_email && r.user_email.toLowerCase().includes(q)) ));
  }

  function refresh(){
    setFilter('');
    setLoading(true); setError(null);
    api.get('/clerk/bookings', { params: { limit: 1000 } }).then(r=>{
      const data = Array.isArray(r.data) ? r.data : (r.data && r.data.data ? r.data.data : []);
      setResults(data);
    }).catch(e=>{ setError('Failed to refresh'); setResults([]); }).finally(()=>setLoading(false));
  }

  return (
    <div className="p-4 bg-white rounded shadow mt-4">
      <div className="flex justify-between items-center mb-3">
        <h4 className="font-semibold">Manage Bookings</h4>
        <div className="flex items-center gap-2">
          <button className="btn btn-sm" onClick={refresh}>Refresh</button>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
        </div>
      </div>

      <div className="mb-3 flex gap-2">
        <input className="border p-2 rounded flex-1" placeholder="Filter by id, user, or event" value={filter} onChange={e=>setFilter(e.target.value)} />
        <button className="btn btn-outline" onClick={applyFilter}>Search</button>
      </div>

      {loading && <div className="text-sm text-gray-600">Loading bookings…</div>}
      {error && <div className="text-red-600 mb-2">{error}</div>}

      {!loading && results.length === 0 && <div className="text-sm text-gray-600">No bookings found</div>}

      {!loading && results.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full table-auto text-sm">
            <thead>
              <tr className="text-left bg-gray-50">
                <th className="p-2">ID</th>
                <th className="p-2">User</th>
                <th className="p-2">Event</th>
                <th className="p-2">Seats</th>
                <th className="p-2">Payment Status</th>
                <th className="p-2">Amount</th>
                <th className="p-2">Created</th>
                <th className="p-2">Updated</th>
                <th className="p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {results.map(r => (
                <tr key={r.id} className="border-t hover:bg-gray-50">
                  <td className="p-2">{r.id}</td>
                  <td className="p-2">{r.user_email || r.user_id}</td>
                  <td className="p-2">{r.event_name || r.event_id}</td>
                  <td className="p-2">{r.seats}</td>
                  <td className="p-2">{r.payment_status}</td>
                  <td className="p-2">{r.amount != null ? `₹${r.amount}` : '-'}</td>
                  <td className="p-2">{r.createdAt || r.created_at || '-'}</td>
                  <td className="p-2">{r.updatedAt || r.updated_at || '-'}</td>
                  <td className="p-2">
                    <div className="flex gap-2">
                      <button className="btn btn-xs btn-ghost" onClick={()=>setSelected(r)}>Details</button>
                      <button className="btn btn-xs" onClick={()=>navigate('/bookings?id=' + r.id)}>Manage</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className="modal-overlay fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 w-full max-w-2xl shadow-lg">
            <div className="flex justify-between items-center">
              <h4 className="font-semibold">Booking #{selected.id}</h4>
              <button className="btn btn-ghost" onClick={()=>setSelected(null)}>Close</button>
            </div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className="text-sm text-gray-600">User</div>
                <div className="font-medium">{selected.user_email || selected.user_id}</div>
                <div className="text-sm text-gray-500">Event: {selected.event_name || selected.event_id || '-'}</div>
                <div className="text-sm text-gray-500">Seats: {selected.seats}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Timing</div>
                <div className="font-medium">{selected.start_time || selected.createdAt || '-'}</div>
                <div className="text-sm text-gray-500">Status: {selected.payment_status || '-'}</div>
                <div className="text-sm text-gray-500">Amount: {selected.amount ? `₹${selected.amount}` : '-'}</div>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <button className="btn btn-primary" onClick={()=>{ setSelected(null); navigate('/bookings?id=' + selected.id); }}>Open in Bookings</button>
              <button className="btn btn-ghost" onClick={()=>setSelected(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
